// Step 3 — net disposable income (CAXeng.md §2.1, FUN_10014a84).
import type { PartyIndex } from '@divorceos/schema';
import type { NCase, NParty } from './normalize.js';
import { capGainFloored } from './taxes.js';
import type { TaxOutcome } from './taxes.js';

export interface PartyNets {
  gross: number;
  /** Row 6 total (with the capital-loss floor). */
  otherTaxable: number;
  /** Rows 7 + 8 (TANF/CS received + other non-taxable). */
  nonTaxable: number;
  /** Guideline deductions other than taxes (rows 12–14, 21–24, hardship). */
  deductions: number;
  hardshipTotal: number;
  /** Net incl. "CS-only" income (DAT_10028314): used by the CS/SS formulas. */
  formulaNet: number;
  /** Displayed net disposable (out+0xc): formulaNet − CS-only income. */
  netDisposable: number;
  csOnly: number;
}

/** §2.1 gross income (FUN_10006123). */
export function grossIncome(p: NParty): number {
  const o = p.in.otherTaxable;
  return p.in.wages + p.in.selfEmployment + p.csOnlyIncome + p.in.nonTaxable.taxExemptInterest + p.in.nonTaxable.other
    + o.otherInvestment + o.nonQualifiedDividends + o.other + o.rental + o.royalty + o.partnerAlimonyReceived
    + o.alimonyReceivedFedNonTaxable + o.alimonyReceivedFedTaxable + o.unemployment + o.socialSecurity + o.interest + o.qualifiedDividends
    + capGainFloored(o, p.irs === 'MFS');
}

export function otherTaxableIncome(p: NParty): number {
  const o = p.in.otherTaxable;
  return o.otherInvestment + o.nonQualifiedDividends + o.other + o.rental + o.royalty + o.partnerAlimonyReceived
    + o.alimonyReceivedFedNonTaxable + o.alimonyReceivedFedTaxable + o.unemployment + o.socialSecurity + o.interest + o.qualifiedDividends
    + o.rateGain28 + o.unrecaptured1250 + capGainFloored(o, p.irs === 'MFS');
}

export function nonTaxableIncome(p: NParty): number {
  return p.in.tanfReceived + p.in.childSupportReceived + p.in.nonTaxable.forChildSupportOnly + p.in.nonTaxable.taxExemptInterest + p.in.nonTaxable.other;
}

/** §2.1 recovered guideline deduction list (excluding taxes and the FC 4071(b) line). */
export function guidelineDeductions(p: NParty): number {
  const i = p.in;
  return i.ssPaidPrior.thisSpouseFedDeductible + i.ssPaidPrior.thisSpouseFedNonDeductible + i.ssPaidPrior.partnerByThisSpouse
    + i.csPaidPrior + i.healthInsurance.paidByParty + i.healthInsurance.wageDeductionPreTax + i.unionDues
    + i.mandatoryRetirement.other + i.mandatoryRetirement.qualifiedAti
    + p.hardshipItems + i.otherGuidelineDeductions.jobRelated + i.otherGuidelineDeductions.other;
}

/**
 * Net disposable income of party p given its taxes and the FC 4071(b) hardship in force
 * (computed value when hardship.calculate, else the entered total).
 */
export function netDisposable(nc: NCase, p: PartyIndex, tax: TaxOutcome, hardshipCalc: number): PartyNets {
  const np = nc.parties[p];
  const gross = grossIncome(np);
  const deductions = guidelineDeductions(np);
  let net = gross - tax.incomeTax - tax.employmentTax - deductions;
  let fc4071b: number;
  if (np.in.hardship.calculate) {
    fc4071b = Math.max(0, hardshipCalc);
    net = Math.max(net - fc4071b, 0);
  } else {
    fc4071b = np.in.hardship.totalFc4071b;
    net -= fc4071b;
  }
  // CP Deduction allocation (0x1b7c == 4): the party's own child-care items come off the DISPLAYED net (out+0xc,
  // which also seeds the adjusted nets) but not the raw formula nets DAT_10028314 used by the unadjusted CS/SS.
  const cpDeduction = nc.c.settings.cceAllocation === 'CP_DEDUCTION' && nc.nkids > 0 ? np.addOnItems : 0;
  return {
    gross, otherTaxable: otherTaxableIncome(np), nonTaxable: nonTaxableIncome(np), deductions: deductions + fc4071b,
    hardshipTotal: fc4071b + np.hardshipItems, formulaNet: net, netDisposable: net - np.csOnlyIncome - cpDeduction, csOnly: np.csOnlyIncome,
  };
}
